import type { ChartEventsOptions, Options } from 'highcharts';
import * as Highcharts from 'highcharts';

import type { HighchartsPaletteOptions } from '@framework/analytics/utils/highcharts-palette-for-theme.util';

import { mergeLegacyChartDestroyForReflow } from '@jepto/shared/utils/legacy-chart-reflow.util';

/**
 * Highcharts v13 defaults to `palette.colorScheme: 'light dark'`, which follows
 * OS `prefers-color-scheme`. Legacy Jepto portfolio charts (KPI, budget, etc.)
 * use app chrome styling and must stay on a white surface.
 */
export const jeptoLegacyChartPaletteOptions: HighchartsPaletteOptions = {
	palette: {
		colorScheme: 'light',
		light: {
			backgroundColor: '#ffffff',
		},
	},
};

let configured = false;

/** Global default for legacy Jepto app charts. Report builder charts override per-chart via ThemeService. */
export function configureJeptoLegacyHighcharts(): void {
	if (configured) {
		return;
	}
	Highcharts.setOptions({
		...jeptoLegacyChartPaletteOptions,
		chart: {
			backgroundColor: '#ffffff',
		},
	});
	configured = true;
}

/** Legacy chart options accept loose shapes from historical call sites; return is typed for Highcharts consumers. */
export function withJeptoLegacyChartSurface(
	options: Record<string, unknown>
): Partial<Options> {
	const chart =
		options['chart'] && typeof options['chart'] === 'object'
			? (options['chart'] as Record<string, unknown>)
			: {};
	const events = mergeLegacyChartDestroyForReflow(
		chart['events'] as ChartEventsOptions | undefined
	);
	return {
		...jeptoLegacyChartPaletteOptions,
		...options,
		chart: {
			...chart,
			backgroundColor: '#ffffff',
			events,
		},
	} as Partial<Options>;
}

/** @deprecated Use {@link withJeptoLegacyChartSurface} */
export const withLegacyKpiChartSurface = withJeptoLegacyChartSurface;

type MinorTickAxis = Highcharts.Axis & {
	pane?: unknown;
	options: Highcharts.AxisOptions & { minorTicks?: boolean };
};

let highchartsMorePaneGuardInstalled = false;

/**
 * Call after `highcharts/highcharts-more` is loaded (e.g. budget gauge charts).
 * highcharts-more wraps `Axis.prototype.getMinorTickInterval` and reads
 * `this.pane.hasSeriesType` unguarded; pane-less axes throw on resize reflow.
 * See JEP-10708.
 */
export function installHighchartsMorePaneGuard(): void {
	if (highchartsMorePaneGuardInstalled) {
		return;
	}
	highchartsMorePaneGuardInstalled = true;
	Highcharts.wrap(
		Highcharts.Axis.prototype,
		'getMinorTickInterval',
		function (
			this: MinorTickAxis,
			proceed: (...args: unknown[]) => unknown,
			...args: unknown[]
		) {
			if (this.pane) {
				return proceed.apply(this, args);
			}
			const { minorTicks, minorTickInterval } = this.options;
			if (minorTicks === true) {
				return minorTickInterval ?? 'auto';
			}
			if (minorTicks === false) {
				return undefined;
			}
			return minorTickInterval;
		}
	);
}
